import { AlertTriangle } from 'lucide-react';

/** どの非本番環境かは `VITE_ENV_LABEL` の明示フラグでのみ判定する (文字列マッチ等の推測はしない)。
 *  `.env.production` には未設定 = 本番扱い。main.tsx (document.title prefix) 等でも共有利用する。 */
const ENV_LABEL = import.meta.env.VITE_ENV_LABEL;

export const IS_STAGING = ENV_LABEL === 'staging';
/** 営業提案用デモ環境 (docs/design/v-demo-environment-plan.md)。実データではなく架空データのため
 *  stagingと同様「本番ではない」ことを常時視認できるようにする。 */
export const IS_DEMO = ENV_LABEL === 'demo';

type EnvBadgeConfig = {
  shortLabel: string;
  longLabel: string;
  /** ヘッダーpillのhover tooltip (title属性、短文) */
  title: string;
  /** 破壊的操作の確認ダイアログでの再掲テキスト (常時可視、より明示的な文言) */
  dialogText: string;
  stripClass: string;
  pillBgClass: string;
  pillTextClass: string;
  dialogBorderClass: string;
};

// Tailwind JITは動的に組み立てたクラス名 (`bg-env-${label}` 等) を検出できないため、
// クラス名は必ずリテラルで列挙する
const BADGES: Record<string, EnvBadgeConfig> = {
  staging: {
    shortLabel: 'STG',
    longLabel: 'STAGING',
    title: '現在ステージング環境です',
    dialogText: '現在の環境: ステージング（本番ではありません）',
    stripClass: 'bg-env-staging',
    pillBgClass: 'bg-env-staging-bg',
    pillTextClass: 'text-env-staging-text',
    dialogBorderClass: 'border-env-staging',
  },
  demo: {
    shortLabel: 'DEMO',
    longLabel: 'DEMO',
    title: '現在デモ環境です (実データではありません)',
    dialogText: '現在の環境: デモ（実データではありません）',
    stripClass: 'bg-env-demo',
    pillBgClass: 'bg-env-demo-bg',
    pillTextClass: 'text-env-demo-text',
    dialogBorderClass: 'border-env-demo',
  },
};

/** 現在の環境に対応するバッジ設定 (本番の場合は null)。main.tsx のタイトル prefix や
 *  AppLayout.tsx の上部ストリップ等、複数箇所で環境ラベルを共有するための単一の参照点。 */
export const activeEnvBadge = ENV_LABEL ? BADGES[ENV_LABEL] ?? null : null;

export type EnvironmentBadgeProps = {
  /** header: ヘッダー内の pill バッジ (常時表示) / dialog: 破壊的操作の確認ダイアログでの再掲 */
  variant: 'header' | 'dialog';
};

export function EnvironmentBadge({ variant }: EnvironmentBadgeProps) {
  if (!activeEnvBadge) return null;

  if (variant === 'header') {
    return (
      <span
        className={`inline-flex shrink-0 items-center gap-1 rounded-full ${activeEnvBadge.pillBgClass} px-2 py-0.5 text-xs font-semibold ${activeEnvBadge.pillTextClass}`}
        title={activeEnvBadge.title}
      >
        <AlertTriangle size={12} />
        <span className="sm:hidden">{activeEnvBadge.shortLabel}</span>
        <span className="hidden sm:inline">{activeEnvBadge.longLabel}</span>
      </span>
    );
  }

  return (
    <div
      className={`mb-3 flex items-center gap-1.5 rounded border ${activeEnvBadge.dialogBorderClass} ${activeEnvBadge.pillBgClass} px-2.5 py-1.5 text-xs font-semibold ${activeEnvBadge.pillTextClass}`}
    >
      <AlertTriangle size={12} className="shrink-0" />
      {activeEnvBadge.dialogText}
    </div>
  );
}
